// All website copy and links in one place.
// Copy rules: UK English, no dashes, no till brand names, no integrations, no Xero.

export const SITE_URL = "https://barloapp.co.uk";
export const APP_URL = "https://app.barloapp.co.uk";
export const LOGIN_URL = "https://app.barloapp.co.uk/login";
export const REGISTER_URL = "https://app.barloapp.co.uk/register";
export const registerUrl = (plan, billing) =>
  `${REGISTER_URL}?plan=${plan}&billing=${billing === "annual" ? "annual" : "monthly"}`;

export const CONTACT_EMAIL = "hello@barloapp.co.uk";
export const SUPPORT_EMAIL = "support@barloapp.co.uk";
export const PRIVACY_EMAIL = "privacy@barloapp.co.uk";
export const NOREPLY_EMAIL = "noreply@barloapp.co.uk";

export const COMPANY = {
  legalName: "Intuo Hospitality Ltd",
  number: "15027152",
  office: "2 Claremount, Leeds, LS6 4EF",
  ico: "ZC280191",
};

// Full FAQ page, grouped. Home shows HOME_FAQS (a selection of these).
export const FAQ_GROUPS = [
  {
    "title": "Getting started",
    "items": [
      {
        "q": "What is Barlo?",
        "a": "Barlo is drinks stock control and supplier ordering for UK bars, pubs and restaurants. You count stock on your phone, upload your weekly sales report, and Barlo shows what you have, what each drink makes you and what to order from each supplier."
      },
      {
        "q": "Who is Barlo for?",
        "a": "Independent bars, pubs and restaurants, typically around 70 to 100 covers, and small groups of 3 or 4 venues. Each plan covers one venue. If you run more than one, email hello@barloapp.co.uk and we'll talk it through."
      },
      {
        "q": "How do I set Barlo up?",
        "a": "Add your drinks menu, a few supplier invoices and a sales report. Barlo reads them, matches names that mean the same drink, and builds your product list with sell prices, costs, case sizes, suppliers and suggested par levels. You check anything it isn't sure about, then you're ready for your first count."
      },
      {
        "q": "Will you set Barlo up for me?",
        "a": "Yes, if you'd like us to. Start your free trial, email your drinks menu, a few supplier invoices and a recent sales report to hello@barloapp.co.uk, and add us as a manager in Settings, Team. We build your product list and par levels with our own login, so no passwords are shared. You check it, remove us when we're done, and you're ready for your first count. There's no extra charge."
      },
      {
        "q": "Does Barlo do food stock?",
        "a": "No. Barlo is built for drinks: spirits, wine, beer, kegs, soft drinks and cocktails. Keep food stock in whatever you use today."
      },
      {
        "q": "Do I need a card to start the free trial?",
        "a": "No. You get 14 days with everything in Pro and no card. Two days before the end we email you to choose Starter or Pro. If you don't choose, your account pauses with no charges and your data is kept for 90 days."
      },
      {
        "q": "Does Barlo work on my phone?",
        "a": "Yes. Barlo is designed for the phone and works on desktop too. iPhone and Android apps are being prepared."
      }
    ]
  },
  {
    "title": "Stocktakes",
    "items": [
      {
        "q": "How do I do a stocktake with Barlo?",
        "a": "Open Count stock and film each shelf, fridge or cellar area while you talk, for example “two cases of prosecco behind the kegs”. Barlo counts it and shows you the numbers. You check them and confirm. If you'd rather, you can type the numbers instead."
      },
      {
        "q": "Do I have to trust what Barlo counts?",
        "a": "No. Nothing is saved until you've checked it. Barlo shows each count for you to confirm or change, so you stay in control of the numbers."
      },
      {
        "q": "Can I count the bar, cellar and fridges separately?",
        "a": "Yes. Add areas like Bar, Cellar and Fridge and count them one at a time. Barlo adds them up to one total for the venue. Kegs and cellar stock are tracked too."
      },
      {
        "q": "How often should a bar do a stocktake?",
        "a": "Many bars count weekly, often on a Monday, so sales and orders line up week to week. With Barlo you can also set a regular count day for the team."
      }
    ]
  },
  {
    "title": "Sales, orders and suppliers",
    "items": [
      {
        "q": "How do my sales get into Barlo?",
        "a": "Upload your weekly sales report as a CSV, Excel file, PDF or photo. Barlo matches each line to your products, takes the stock off and shows you anything it couldn't match before anything changes."
      },
      {
        "q": "Does Barlo connect to my till?",
        "a": "You don't need a connection. Once a week, download the sales report from your till and upload it to Barlo. CSV, Excel, PDF or a photo of the report all work. The first upload takes a little longer while you check how the drink names match. After that Barlo remembers, and each weekly upload takes a couple of minutes."
      },
      {
        "q": "Does Barlo handle cocktails?",
        "a": "Yes. Set up each cocktail as a recipe once. Every one sold takes each ingredient off stock, measure by measure, and you see the cost per serve."
      },
      {
        "q": "How does Barlo build my order?",
        "a": "From your par levels: anything below par is added to the order for its supplier, in case sizes. On Pro, Barlo also suggests changes using your sales trend, the weather and your bookings."
      },
      {
        "q": "Can I send orders to all my suppliers at once?",
        "a": "Yes. Barlo groups the order by supplier. Check and edit each one, then send every supplier email with one button."
      },
      {
        "q": "Can Barlo see my bookings?",
        "a": "Yes. Link ResDiary, OpenTable, SevenRooms or Google Calendar once and Barlo shows the covers coming in this week. It only reads your bookings and never changes them."
      }
    ]
  },
  {
    "title": "Money and GP",
    "items": [
      {
        "q": "How does Barlo help my GP?",
        "a": "Barlo shows what each drink costs you per serve and what it makes you, including cocktails. On Pro, when a delivery invoice is checked against your order, price rises are flagged and you see GP% trends and alerts when a drink's GP drops."
      },
      {
        "q": "How do I calculate GP on drinks?",
        "a": "Take VAT off the menu price, subtract the cost per serve, then divide by the price without VAT. Our free bar GP calculator does it for you, with the price you need to hit your target."
      },
      {
        "q": "Will Barlo show me where stock is going missing?",
        "a": "Yes. The weekly shrinkage report compares what you sold with what's gone from the shelf, product by product. Waste is recorded separately, in pounds."
      }
    ]
  },
  {
    "title": "Plans, staff and data",
    "items": [
      {
        "q": "What's the difference between Starter and Pro?",
        "a": "Starter (£29 a month) covers counting, ordering to par, sales upload, cocktails, bookings, core reports and Barlo AI to answer your questions, for up to 150 products and 3 staff plus the owner. Pro (£39 a month) adds unlimited products, Barlo AI that does jobs for you and counts by voice, AI order suggestions, the weather forecast, the Monday summary email, price rise alerts, invoice matching and advanced reports, for up to 5 staff plus the owner."
      },
      {
        "q": "Can my staff use Barlo?",
        "a": "Yes. Starter includes up to 3 staff logins and Pro up to 5, plus the owner, each with a role. Staff can log in with a PIN and do counts and sales."
      },
      {
        "q": "If I change plan, do I lose anything?",
        "a": "No. Moving from Starter to Pro keeps all your products, stock and history. The same goes after the trial: if you take a break, your data is kept for 90 days."
      },
      {
        "q": "Is my data safe?",
        "a": "Your data is encrypted in transit and at rest, handled under UK GDPR, and never sold. You can export it as CSV whenever you like."
      },
      {
        "q": "How does Barlo AI use my data?",
        "a": "Barlo AI is powered by Claude, made by Anthropic. On Starter it answers questions; on Pro it also does jobs for you and counts by voice. It asks your permission before any data is sent, and it prepares jobs for you to confirm rather than doing them on its own."
      },
      {
        "q": "Is there a contract?",
        "a": "No. Pay monthly or yearly (save 10%) and cancel any time. You keep access to the end of the period you paid for."
      }
    ]
  }
];

const ALL_FAQS = FAQ_GROUPS.flatMap((g) => g.items);
const pick = (q) => ALL_FAQS.find((it) => it.q === q);
export const HOME_FAQS = ["What is Barlo?", "Do I need a card to start the free trial?", "How do I do a stocktake with Barlo?", "How do my sales get into Barlo?", "How does Barlo build my order?", "How does Barlo help my GP?", "Does Barlo connect to my till?", "Will you set Barlo up for me?", "Does Barlo do food stock?", "Can my staff use Barlo?"].map(pick);

export const PRICING_FAQS = [
  {
    q: "What happens after the free trial?",
    a: "Two days before it ends we email you to choose Starter or Pro. If you don't choose, your account pauses with no charges and your data is kept for 90 days.",
  },
  {
    q: "How does yearly billing work?",
    a: "Pay for 12 months up front and save 10%: Starter £313 a year, Pro £421 a year.",
  },
  {
    q: "Can I use a promo code with yearly billing?",
    a: "No. Discounts can't be combined, so it's either the yearly saving or a promo code.",
  },
  {
    q: "Can I move between plans?",
    a: "Yes, in Barlo in a browser under Settings, Plan and Billing. Moving from Starter to Pro keeps all your products, stock and history.",
  },
  {
    q: "How do I cancel?",
    a: "In Barlo in a browser, under Settings, Plan and Billing. You keep access to the end of the period you paid for.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "It's kept for 90 days after your plan ends, so you can pick up where you left off. After that it's deleted, and we email you a week before.",
  },
  {
    q: "Is there a limit on products?",
    a: "Starter covers up to 150 drinks products, enough for most pubs and bars. Archived products and drafts don't count. Need more? Pro has no limit, and moving up keeps everything you've set up. If you go over, nothing is removed; you just can't add more until you archive some or move to Pro.",
  },
  {
    q: "I run more than one venue.",
    a: "Each plan covers one venue. Email hello@barloapp.co.uk and we'll talk it through.",
  },
];

export const PLANS = [
  {
    slug: "starter",
    name: "Starter",
    for: "For a single bar, pub or restaurant getting off paper.",
    weekly: "Less than £7 a week.",
    staff: "Up to 3 staff, plus the owner",
    monthly: "£29",
    annual: "£313",
    annualPerMonth: "Works out at £26.08 a month",
    features: [
      "Film & count, 3 a month (resets on the 1st)",
      "Barlo AI answers your questions about stock, sales and orders",
      "Voice counting, 3 a month to try",
      "Set up from your documents and bulk import",
      "Up to 150 drinks products, bottle and barcode scanner",
      "Manual and scheduled stocktakes",
      "Weekly sales upload, cocktails taken off by recipe",
      "Sees your bookings: covers for the next 7 days",
      "Orders to par, sent to every supplier in one tap",
      "Cellar and keg tracking, low stock alerts",
      "Stock, usage and wastage reports, weekly shrinkage, CSV export",
      "Email support, reply within one working day",
    ],
  },
  {
    slug: "pro",
    name: "Pro",
    popular: true,
    for: "For busy venues that want Barlo to build the order and watch the GP.",
    weekly: "About £9 a week.",
    staff: "Up to 5 staff, plus the owner",
    monthly: "£39",
    annual: "£421",
    annualPerMonth: "Works out at £35.08 a month",
    head: "Everything in Starter, plus",
    features: [
      "Unlimited drinks products",
      "Barlo AI does jobs for you, unlimited Film & count and voice counting",
      "AI order suggestions from sales trend, weather and bookings",
      "7-day weather forecast to plan your orders",
      "Monday summary email: sales, profit, best sellers, what's low, waste",
      "Supplier price history and price rise alerts",
      "Delivery tracking and invoice matching",
      "Variance, drink costs, GP% trends and margin alerts",
      "Priority email support, reply the same working day",
    ],
  },
];

// Third card on the pricing page. No price yet: the button emails us.
export const GROUP_PLAN = {
  name: "Group",
  for: "For owners running 3 or more venues.",
  price: "Let's talk",
  note: "A price based on how many venues you run.",
  features: [
    "Everything in Pro, at every venue",
    "One price for the whole group",
    "We help you set up each venue",
  ],
  cta: "Email us",
};

// [label, starter, pro]; true = yes, false = no, string = value
export const COMPARISON = [
  { group: "Your venue", rows: [
    ["Venues", "1", "1"],
    ["Staff logins with roles", "Up to 3, plus the owner", "Up to 5, plus the owner"],
    ["Drinks products", "Up to 150", "Unlimited"],
  ]},
  { group: "Counting", rows: [
    ["Film & count", "3 a month, resets on the 1st", "Unlimited"],
    ["Voice counting", "3 a month to try", "Unlimited"],
    ["Manual and scheduled stocktakes", true, true],
    ["Bottle and barcode scanner", true, true],
    ["Cellar and keg tracking", true, true],
  ]},
  { group: "Sales and ordering", rows: [
    ["Weekly sales upload", true, true],
    ["Orders to par, sent in one tap", true, true],
    ["Bookings: covers for the next 7 days", true, true],
    ["7-day weather forecast", false, true],
    ["AI order suggestions", false, true],
    ["Delivery tracking and invoice matching", false, true],
    ["Supplier price history and price rise alerts", false, true],
  ]},
  { group: "Reports", rows: [
    ["Stock, usage and wastage reports", true, true],
    ["Weekly shrinkage report", true, true],
    ["Variance, drink costs, GP% trends, margin alerts", false, true],
    ["CSV export", true, true],
    ["Monday summary email", false, true],
  ]},
  { group: "Barlo AI and support", rows: [
    ["Barlo AI", "Answers questions", "Answers questions and does jobs for you"],
    ["Email support", "1 working day", "Same working day"],
  ]},
];

// Features page. plan: "all" | "pro" | custom chips
export const FEATURE_GROUPS = [
  { title: "Counting stock", items: [
    { icon: "video", title: "Film & count", body: "Film each shelf, fridge or cellar area and talk while you film. Barlo counts it; you check it and confirm.", chips: [["Starter: 3 a month", ""], ["Pro: unlimited", "pro"]] },
    { icon: "keyboard", title: "Manual and scheduled stocktakes", body: "Type the numbers when you'd rather, count by area, and set a regular count day for the team.", plan: "all" },
    { icon: "scan", title: "Bottle and barcode scanner", body: "Point the camera at a bottle or barcode and Barlo finds the product.", plan: "all" },
    { icon: "barrel", title: "Cellar and keg tracking", body: "Kegs and cellar stock counted alongside the bar, added up to one total for the venue.", plan: "all" },
    { icon: "cocktail", title: "Cocktails and recipes", body: "Set up a recipe once. Each cocktail sold takes every ingredient off stock, measure by measure.", plan: "all" },
    { icon: "trend", title: "Weekly shrinkage report", body: "What you sold against what's gone from the shelf, product by product.", plan: "all" },
  ]},
  { title: "Setting up", items: [
    { icon: "file", title: "Set up from your documents", body: "Add your drinks menu, supplier invoices and a sales report. Barlo builds your product list with prices, costs, case sizes, suppliers and suggested par levels.", plan: "all" },
    { icon: "upload", title: "Bulk product import", body: "Already have a product list? Bring it in from a file in one go.", plan: "all" },
    { icon: "box", title: "Your drinks products", body: "Cost price, sell price, case size, supplier and par level for every product. Archived products and drafts don't count towards the limit.", chips: [["Starter: up to 150", ""], ["Pro: unlimited", "pro"]] },
  ]},
  { title: "Sales and ordering", items: [
    { icon: "receipt", title: "Weekly sales upload", body: "Upload your sales report as a CSV, Excel file, PDF or photo. Barlo takes the stock off and shows you anything it couldn't match.", plan: "all" },
    { icon: "calendar", title: "Sees your bookings", body: "Link ResDiary, OpenTable, SevenRooms or Google Calendar once and Barlo shows the covers coming in this week. It only reads your bookings.", plan: "all" },
    { icon: "cart", title: "Orders to par, sent in one tap", body: "An order list for each supplier, built from your par levels. Check it, change it, then email every supplier with one button.", plan: "all" },
    { icon: "truck", title: "Supplier management", body: "Contacts, products and order emails for each supplier in one place.", plan: "all" },
    { icon: "cloud", title: "7-day weather forecast", body: "The week ahead next to your order, so a sunny weekend doesn't catch you short on rosé.", plan: "pro" },
    { icon: "sparkle", title: "AI order suggestions", body: "Barlo adjusts the order using your sales trend, the weather and your bookings, and tells you why.", plan: "pro" },
    { icon: "check", title: "Delivery tracking and invoice matching", body: "Check the delivery and invoice against what you ordered. Short deliveries and wrong prices stand out.", plan: "pro" },
  ]},
  { title: "Money and reports", items: [
    { icon: "pound", title: "Price rise alerts", body: "When an invoice is checked against the order, price increases are flagged, with the supplier's price history for every product.", plan: "pro" },
    { icon: "chart", title: "Advanced reports", body: "Variance, drink costs, GP% trends and margin alerts when a drink's GP drops.", plan: "pro" },
    { icon: "list", title: "Core reports", body: "Stock value, usage and wastage in pounds, plus what it costs to get back to par.", plan: "all" },
    { icon: "bell", title: "Low stock alerts", body: "Know when something drops below par before Friday night does it for you.", plan: "all" },
    { icon: "mail", title: "Monday summary email", body: "Sales, profit, best sellers, what's low and waste, in your inbox every Monday.", plan: "pro" },
    { icon: "download", title: "CSV export", body: "Take your numbers to your accountant or your own spreadsheet whenever you like.", plan: "all" },
  ]},
  { title: "Barlo AI and your team", items: [
    { icon: "sparkle", title: "Barlo AI", body: "Ask about your stock, sales and orders. On Pro, tell it what to do: “we sold 4 Negronis”. It prepares the job, you confirm, and it counts by voice. Powered by Claude, made by Anthropic, and it asks before any data is sent.", chips: [["Starter: answers questions", ""], ["Pro: does jobs, counts by voice", "pro"]] },
    { icon: "users", title: "Staff logins and roles", body: "Owner, manager and staff roles, PIN login on a shared phone or tablet, staff counts and sales.", chips: [["Starter: 3 plus owner", ""], ["Pro: 5 plus owner", "pro"]] },
    { icon: "undo", title: "Undo, help tips, light and dark", body: "Small things that make it easy to use at the end of a long shift.", plan: "all" },
  ]},
];
